import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import {
  OptimizationChartOptionsProps,
  optimizationChartOptions,
} from '@features/campaigns/subdomains/visitation/configuration/optimizationChartOptions';
import { subdimensionId } from '@features/campaigns/subdomains/visitation/utils';
import { clampNegativeToZero } from '@utils/valueTransformers';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { OVERALL_OPTION, ValueFromSubdimension } from './PerformersCard';

type BaseSubdimension = {
  name: string;
  value: string;
};

export type BaseReport = {
  reportDate: string;
};

export type OptimizationChartProps<T, U> = {
  reports: T[];
  reportDates: string[];
  subdimensions: (U & { color: string })[];
  selectedSubdimensions: Record<string, boolean>;
  yAxisLabel: string;
  getValueFromSubdimension: ValueFromSubdimension<U>;
  getValueFromReport: (report: T) => number | null;
  getSubdimensionsFromReport: (report: T) => U[];
};

export const OptimizationChart = <
  T extends BaseReport,
  U extends BaseSubdimension,
>(
  props: OptimizationChartProps<T, U>
) => {
  const dimensionSeries: OptimizationChartOptionsProps['series'] =
    props.subdimensions.map(subdimension => {
      const name = [
        apiSlugToDimensionName[
          subdimension.name as keyof typeof apiSlugToDimensionName
        ],
        subdimension.value,
      ].join(' | ');

      const data = props.reports.map(report => {
        const localSubdimension = props
          .getSubdimensionsFromReport(report)
          .find(
            d => d.name === subdimension.name && d.value === subdimension.value
          );

        return {
          value: localSubdimension
            ? clampNegativeToZero(
                props.getValueFromSubdimension(localSubdimension) ?? 0
              )
            : 0,
          date: report.reportDate,
        };
      });

      return {
        name,
        data,
        visible:
          props.selectedSubdimensions[subdimensionId(subdimension)] ?? true,
        color: subdimension.color,
      };
    });

  const overallSeries: OptimizationChartOptionsProps['series'][number] = {
    name: 'Overall',
    visible:
      props.selectedSubdimensions[subdimensionId(OVERALL_OPTION)] ?? true,
    color: OVERALL_OPTION.color,
    data: props.reports.map(report => ({
      value: clampNegativeToZero(props.getValueFromReport(report) ?? 0),
      date: report.reportDate,
    })),
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={optimizationChartOptions({
        series: [overallSeries, ...dimensionSeries],
        yAxisLabel: props.yAxisLabel,
        dates: props.reportDates,
      })}
      containerProps={{
        ['data-testid']: 'optimization-chart',
        style: {
          width: '100%',
        },
      }}
    />
  );
};
